
import { Component, Prop, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import MANAGEMENT_CLIENTS from "@/apollo/projects/ManagementProjects.gql";
import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";
import ProjectModal from "@/components/companyProjects/ProjectModalNew.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import UIButton from "@/components/button/ui-button.vue";
import {
  User,
  Company,
  Project,
  Query_RootProjectArgs,
} from "@/generated/graphql";
import DefaultContentLayout from "@/components/scaffold/DefaultContentLayoutNew.vue";
import ProjectCollectionFilters from "@/components/companyProjects/ProjectCollectionFiltersNew.vue";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import ContractorChips from "@/components/companyProjects/ContractorChips.vue";
import ProjectContractorsCardItem from "@/components/projects/ProjectContractorsCardItem.vue";
import ProjectGroupsCardItem from "@/components/projects/ProjectGroupsCardItem.vue";
import ProjectModalWithButton from "@/components/companyProjects/ProjectModalWithButton.vue";
import projectsFiltersTemplate from "@/data/filtersTemplates/projectsFiltersTemplate";
import RouteNames from "@/entities/RouteNames";
import ContractorChipData from "@/entities/ContractorChipData";
import Roles from "@/entities/Roles";
import PaginationConfig from "@/entities/PaginationConfig";
import paginationConfig from "@/data/paginationConfig";
import getFullNameOrPlaceholder from "@/helpers/getFullNameOrPlaceholder";
import ProjectsFiltersQuery from "@/entities/filters/ProjectsFiltersQuery";
import {
  getFiltersDataFromQuery,
  joinQueryValues,
} from "@/helpers/router/prepareFiltersQuery";
import { cloneDeep, isEqual } from "lodash-es";
import {
  getPaginationDataFromQuery,
  getPaginationQuery,
} from "@/helpers/router/preparePaginationQuery";
import EmptyListImageProjects from "@/components/icons/emptyListImages/EmptyListImageProjects.vue";
import ProjectCardWrapper from "@/components/projects/ProjectCardWrapper.vue";
import moment from "moment";

interface ProjectFilter extends Query_RootProjectArgs {
  skipRequestsCountQuery: boolean;
}

@Component({
  components: {
    ProjectCardWrapper,
    EmptyListImageProjects,
    ProjectModal,
    DefaultContentLayout,
    ProjectCollectionFilters,
    EmptyListPlaceholder,
    CardPagination,
    LoadingOverlay,
    ContractorChips,
    UIButton,
    ProjectContractorsCardItem,
    ProjectGroupsCardItem,
    ProjectModalWithButton,
  },
  apollo: {
    projects: {
      query: MANAGEMENT_CLIENTS,
      variables() {
        return this.projectFilter;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.project.length;
        return data.project;
      },
    },
  },
})
export default class ColsedClients extends mixins(GetRelativeRouteNameMixin) {
  @Prop({ type: Object, required: true }) readonly company!: Company;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: Number, required: true }) readonly closedTotal!: number;

  expandedDescriptions: { [key: string]: boolean } = {};
  descriptionMaxLength = 100;
  dialog: boolean = false;

  selectedProject?: Project = undefined;

  projects: Project[] | null = null;

  paginationConfig: PaginationConfig = { ...paginationConfig };

  projectFilters: ProjectsFiltersQuery = cloneDeep(projectsFiltersTemplate);
  RouteNames: typeof RouteNames = RouteNames;

  get hasData(): boolean {
    return !this.isLoading && Boolean(this.projects);
  }

  get filtersEmpty(): boolean {
    return isEqual(this.projectFilters, projectsFiltersTemplate);
  }

  get emptyListPlaceholder(): string {
    const entitiesName = "projects";

    if (this.filtersEmpty) {
      return "You currently do not have any closed profiles";
    }

    return `You currently do not have any closed profiles`;
  }

  get dashboardRoute() {
    return {
      name: RouteNames.DASHBOARD,
    };
  }

  get isLoading(): boolean {
    return this.$apollo.queries.projects.loading;
  }

  get managerRouteName(): string {
    return RouteNames.MANAGER_ABOUT;
  }

  get title(): string {
    return `Projects: ${this.paginationConfig.total}`;
  }

  get isManager(): boolean {
    return this.currentUser.role === "MANAGER";
  }

  get empty(): boolean {
    if (!this.projects) {
      return false;
    }

    return this.projects.length === 0;
  }


  get currentUserManagerId(): string {
    return this.currentUser.manager!.id;
  }

  get projectFilter(): ProjectFilter {
    const { page, limit } = this.paginationConfig;
    const nextOffset = page * limit - limit;

    const { createdAt, title, managers, contractors, location } =
      this.projectFilters;

    const filterConditions = [];

    filterConditions.push({
      drafted: {
        _eq: false,
      },
      completed: {
        _eq: true,
      },
       company_id: {
        _eq: this.company.id
      }
    });

    if (managers.length) {
      filterConditions.push({
        manager: {
          id: { _in: managers },
        },
      });
    }

    if (contractors.length) {
      filterConditions.push({
        project_activities: {
          contractor_id: {
            _in: contractors,
          },
        },
      });
    }

    if (location.length) {
      filterConditions.push({
        address: { _ilike: `%${location}%` },
      });
    }

if (this.isManager) {
      filterConditions.push({
        manager: {
          id: { _eq: this.currentUserManagerId },
        },
      });
    }

    if (title) {
      filterConditions.push({
        client_name: { _ilike: `%${title}%` },
      });
    }

    if (createdAt) {
      const startDate = moment(createdAt).utc().startOf("day").toISOString();
      const endDate = moment(createdAt).utc().endOf("day").toISOString();

      const datesConditions = [
        { created_at: { _gte: startDate } },
        { created_at: { _lte: endDate } },
      ];

      filterConditions.push({
        _and: datesConditions,
      });
    }


    return {
      offset: nextOffset,
      limit: limit,
      where: {
        _and: filterConditions,
      },
      skipRequestsCountQuery: !this.isAdmin,
    };
  }

  get isAdmin(): boolean {
    return this.currentUser.role === Roles.ADMIN;
  }

  get queryPropsToWatch(): string {
    return JSON.stringify([this.projectFilters, this.paginationConfig]);
  }

  get queryParams(): {
    filters: ProjectsFiltersQuery;
    pagination: PaginationConfig;
  } {
    const query = this.$route.query;

    const { created_at: createdAt, ...queryParams } = query;

    const queryData = {
      createdAt,
      ...queryParams,
    };

    const filters = getFiltersDataFromQuery<ProjectsFiltersQuery>(
      projectsFiltersTemplate,
      queryData
    );
    const pagination = {
      ...this.paginationConfig,
      ...getPaginationDataFromQuery(query),
    };

    return {
      filters,
      pagination,
    };
  }

  get isChosenPageEmpty(): boolean {
    const collectionEmpty = this.projects?.length === 0;
    const notFirstPage = this.paginationConfig.page !== 1;

    return !this.isLoading && collectionEmpty && notFirstPage;
  }

  @Watch("queryPropsToWatch")
  updateQuery(): void {
    if (this.isChosenPageEmpty) {
      return this.showFirstPage();
    }

    const paginationQuery = getPaginationQuery(this.paginationConfig);

    const { createdAt, location, ...otherFilters } = this.projectFilters;

    const query = joinQueryValues({
      ...paginationQuery,
      ...otherFilters,
      location: location,
      created_at: createdAt,
    });

    if (isEqual(query, this.$route.query)) {
      return;
    }

    this.$router.push({ query }).catch(() => {});
  }

  @Watch("$route.query", { immediate: true })
  onQueryChange(): void {
    const { filters, pagination } = this.queryParams;

    this.projectFilters = filters;
    this.paginationConfig = pagination;
  }

  get requests(): Record<string, number> {
    if (this.projects?.length === 0) {
      return {};
    }

    return Object.fromEntries(
      this.projects!.map((project) => [
        project.id,
        this.getRequestsCount(project),
      ])
    );
  }

  getRequestsCount(project: Project): number {
    if (!project.manager || !this.isAdmin) {
      return 0;
    }

    const requestedContractorsList = project.manager?.contractors.reduce<
      Record<string, number>
    >((uniqueContractors, managerContractor) => {
      const projectActivities =
        managerContractor.contractor.project_activities.filter(
          ({ project_id }) => project_id === project.id
        );

      projectActivities.forEach(({ contractor_id }) => {
        if (!uniqueContractors[contractor_id]) {
          uniqueContractors[contractor_id]++;
        }
      });

      return uniqueContractors;
    }, {});

    return Object.keys(requestedContractorsList).length;
  }

  updateFilters(filters: ProjectsFiltersQuery): void {
    const dataEqual = isEqual(filters, this.projectFilters);

    if (dataEqual) {
      return;
    }

    this.projectFilters = filters;
  }

  resetFilters(): void {
    if (isEqual(this.projectFilters, projectsFiltersTemplate)) {
      return;
    }

    this.projectFilters = cloneDeep(projectsFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

  showFirstPage(): void {
    this.paginationConfig = {
      ...this.paginationConfig,
      page: paginationConfig.page,
    };
  }

  projectContractors(project: Project) {
    const contractors: { [key: string]: ContractorChipData } = {};

    project.project_activities.forEach(({ contractor }) => {
      if (!contractor) {
        return;
      }

      const { id, user, deleted } = contractor;

      if (!contractors[id]) {
        const { given_name, family_name } = user;
        const name = `${given_name} ${family_name}`;

        contractors[id] = {
          id,
          name,
          deleted,
          activityCount: 1,
        };
        return;
      }

      contractors[id].activityCount++;
    });

    return Object.values(contractors);
  }

  projectContractorsNumber(project: any): number {
    return project.total_activities.aggregate.count;
  }

  projectManager(project: Project): string {
    const givenName = project?.manager?.user.given_name;
    const familyName = project?.manager?.user.family_name;
    return getFullNameOrPlaceholder(givenName, familyName);
  }

  projectManagerId(project: Project): string | undefined {
    return project.manager ? project.manager.id : undefined;
  }

  managerClickable(manager: any): boolean {
    return manager && this.currentUser.role == Roles.ADMIN;
  }

  refetchProjects(): void {
    this.$apollo.queries.projects.refetch();
  }
}
